import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  MobileStepper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Rating,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import KeyIcon from '@mui/icons-material/Key';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Google from '@mui/icons-material/Google';
import Apple from '@mui/icons-material/Apple';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import SquareFootRoundedIcon from '@mui/icons-material/SquareFootRounded';
import SpeakerRoundedIcon from '@mui/icons-material/SpeakerRounded';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SEO from '../../components/SEO';
import { userApiClient } from '../../userApiClient';
import { hostApiClient } from '../../hostApiClient';
import { notifyRoomBookingNeedsActionCountChanged } from '../../utils/roomBookingNeedsActionCount';
import { isHostRoomBookingDetailsPath } from '../../utils/roomBookingDetailsPath';
import { apiClient } from '../../publicApiClient';
import { RoomBookingDetails } from '../../models/RoomBookingDetails';
import { RoomBookingStatus } from '../../models/RoomBooking';
import { userRoomCancelReleasesSeries } from '../../utils/userRoomBookingGrouping';
import { RoomBookingChangeProposal, RoomBookingChangeStatus, DeclineChangeRequest } from '../../models/RoomBookingChange';
import BookingCancellationPolicy from '../../components/BookingCancellationPolicy';
import BookingCreatedAtLabel from '../../components/BookingCreatedAtLabel';
import TutorBookingScheduleBlock from '../../components/tutor/TutorBookingScheduleBlock';
import TutorBookingPaymentStatusChips from '../../components/tutor/TutorBookingPaymentStatusChips';
import { shouldShowCancellationPolicyForPayment } from '../../utils/formatCancellationPolicy';
import {
  formatTutorBookingScheduleLabels,
  getTutorBookingPreviousTimeLabel,
  type TutorBookingScheduleLabels,
} from '../../utils/tutorBookingChangeProposal';
import { getAmenityLabel, amenityIcons } from '../../amenities';
import { getGearLabel, gearIcons } from '../../gears';
import { SpaceReviewResponse } from '../../models/SpaceReview';
import { Review } from '../../models/CreateReviewRequest';
import MediaDisplay from '../../components/MediaDisplay';
import VideoPlayer from '../../components/VideoPlayer';
import { isVideoUrl } from '../../utils/mediaUtils';
import { useThemeContext } from '../../contexts/ThemeContext';
import RoomBookingStepperDialog from '../../components/RoomBookingStepperDialog';
import { RoomSummary } from '../../models/Room';
import { BookingPasscodeStatus } from '../../models/SmartLock';
import PassBookingChip from '../../components/passes/PassBookingChip';
import RoomBookingUserAvatar from '../../components/RoomBookingUserAvatar';
import BookingStripeFeeBreakdown from '../../components/booking/BookingStripeFeeBreakdown';
import UserBandAffiliationLine from '../../components/UserBandAffiliationLine';
import SpaceDetailLink from '../../components/SpaceDetailLink';
import ChatWithUserIconButton from '../../components/chat/ChatWithUserIconButton';
import { getCachedProfileDisplay } from '../../services/profileDisplayCache';
import {
  organizationContactHref,
  organizationContactIcon,
  organizationContactOpensInSameTab,
  visibleOrganizationContacts,
} from '../../utils/organizationContact';

export default function RoomBookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isHost = isHostRoomBookingDetailsPath(location.pathname);
  const bookingsListPath = isHost ? '/host/requests' : '/user/bookings';
  const { t, i18n } = useTranslation();
  const { mode } = useThemeContext();
  const [bookingDetails, setBookingDetails] = useState<RoomBookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [passcodeCopied, setPasscodeCopied] = useState(false);
  const [spaceThumbnailUrl, setSpaceThumbnailUrl] = useState<string | null>(null);
  const [spaceDescription, setSpaceDescription] = useState<string | null>(null);
  
  // Review-related state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState<number>(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [existingReview, setExistingReview] = useState<SpaceReviewResponse | null>(null);
  const [hasReviewedSpace, setHasReviewedSpace] = useState(false);
  const [spaceReviewStats, setSpaceReviewStats] = useState<{
    averageRating: number;
    reviewCount: number;
  } | null>(null);
  const [spaceReviews, setSpaceReviews] = useState<Review[]>([]);
  const [allReviewsDialogOpen, setAllReviewsDialogOpen] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Book again state
  const [bookAgainDialogOpen, setBookAgainDialogOpen] = useState(false);
  const [roomSummary, setRoomSummary] = useState<RoomSummary | null>(null);
  const [loadingRoomSummary, setLoadingRoomSummary] = useState(false);
  const [calendarIcsLoading, setCalendarIcsLoading] = useState(false);

  // Change proposal state
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [processingChange, setProcessingChange] = useState<string | null>(null);

  const resolveSpacePresentation = async (details: RoomBookingDetails) => {
    let thumbnailUrl = details.spaceImageUrl?.trim() || null;
    let description = details.spaceDescription?.trim() || null;

    if ((!thumbnailUrl || !description) && details.spaceId) {
      try {
        const summary = await apiClient.getSpaceSummary(details.spaceId);
        if (!thumbnailUrl) {
          thumbnailUrl = summary.imageUrls?.[0]?.trim() || null;
        }
        if (!description) {
          description = summary.description?.trim() || null;
        }
      } catch (summaryError) {
        console.log('Could not load space presentation:', summaryError);
      }
    }

    return { thumbnailUrl, description };
  };

  const refreshSpaceReviewStats = async (spaceId: string) => {
    try {
      const fetchedReviews = await apiClient.getReviews(spaceId);
      setSpaceReviews(fetchedReviews);
      if (fetchedReviews.length > 0) {
        const totalRating = fetchedReviews.reduce((sum, review) => sum + review.rating, 0);
        setSpaceReviewStats({
          averageRating: totalRating / fetchedReviews.length,
          reviewCount: fetchedReviews.length,
        });
      } else {
        setSpaceReviewStats({ averageRating: 0, reviewCount: 0 });
      }
    } catch (reviewStatsError) {
      console.log('No space reviews found or error fetching reviews:', reviewStatsError);
    }
  };

  // Update current time to check if booking has ended (UI only — must not refetch booking details)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = isHost
          ? await hostApiClient.getHostRoomBookingDetails(id)
          : await userApiClient.getRoomBookingDetails(id);
        setBookingDetails(data);
        const presentation = await resolveSpacePresentation(data);
        setSpaceThumbnailUrl(presentation.thumbnailUrl);
        setSpaceDescription(presentation.description);

        if (!isHost) {
          await refreshSpaceReviewStats(data.spaceId);

          const bookingEndTime = new Date(data.endTime);
          const nowUTC = new Date();

          if (bookingEndTime < nowUTC) {
            try {
              const spaceReview = await userApiClient.getSpaceReview(data.spaceId);
              if (spaceReview) {
                setHasReviewedSpace(true);
                setExistingReview(spaceReview);
              }
            } catch (reviewFetchError) {
              console.log('No existing space review found or error fetching review:', reviewFetchError);
            }
          }
        }
      } catch (fetchError) {
        console.error(t('bookingDetails.fetchError', 'Error fetching booking details:'), fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id, isHost, t]);

  // Check if user is on the review route and open review dialog
  useEffect(() => {
    if (isHost) return;
    const onReviewRoute = location.pathname.endsWith('/review');
    
    if (onReviewRoute && bookingDetails && !reviewDialogOpen) {
      setReviewDialogOpen(true);
    }
  }, [location.pathname, bookingDetails, reviewDialogOpen]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !bookingDetails) {
    return (
      <Box sx={{ p: 4 }}>
        <Chip
          label={
            isHost
              ? `← ${t('roomBookingRequests.backToRequests', 'Back to Booking Requests')}`
              : `← ${t('bookingDetails.backToBookings', 'Back to My Bookings')}`
          }
          onClick={() => navigate(bookingsListPath)}
          sx={{
            mb: 2,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'transparent' },
          }}
        />
        <Typography variant="h6" color="error" gutterBottom>
          {t('bookingDetails.loadError', "Couldn't load booking details")}
        </Typography>
      </Box>
    );
  }

  // Only truly "included" gear: price per hour must be exactly 0.
  // Backend may send 0 / null-ish values; treat any positive price as rental (not included).
  const includedRoomGears = bookingDetails.roomGears.filter((g) => Number(g.pricePerH) === 0);

  // API currently mixes date-time semantics:
  // - booking start/end are already localized for display
  // - change proposal times are UTC-like values without "Z"
  // Keep the UTC parser explicit to avoid double timezone shifts.
  const parseApiUtcIso = (iso: string) =>
    new Date(iso.endsWith('Z') ? iso : `${iso}Z`);

  // Calculate duration in minutes
  const calculateDuration = (startTime: string, endTime: string) => {
    const start = parseApiUtcIso(startTime);
    const end = parseApiUtcIso(endTime);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  };

  // Format duration
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return t('bookingDetails.minutesOnly', '{{minutes}} min', { minutes: remainingMinutes });
    } else if (remainingMinutes === 0) {
      return t('bookingDetails.hoursOnly', '{{hours}} hr', { hours, count: hours });
    } else {
      return t('bookingDetails.hoursAndMinutes', '{{hours}} hr {{minutes}} min', { 
        hours, 
        minutes: remainingMinutes,
        count: hours 
      });
    }
  };

  // Format address from booking details
  const formatAddress = (booking: RoomBookingDetails) => {
    const parts = [];
    
    if (booking.spaceAddressLine1) parts.push(booking.spaceAddressLine1);
    if (booking.spaceAddressLine2) parts.push(booking.spaceAddressLine2);
    if (booking.spaceCity) parts.push(booking.spaceCity);
    if (booking.spaceState) parts.push(booking.spaceState);
    if (booking.spaceZipCode) parts.push(booking.spaceZipCode);
    
    return parts.join(', ');
  };

  const formatPasscodeWindow = (start: string, end: string) => {
    const parseUtcLike = (iso: string) => new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
    const timezone =
      bookingDetails?.timezone ||
      bookingDetails?.spaceTimezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formatInTimezone = (iso: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone,
      }).format(parseUtcLike(iso));
    const from = formatInTimezone(start);
    const to = formatInTimezone(end);
    return `${from} - ${to}`;
  };

  // Get booking status color (matching other pages)
  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case RoomBookingStatus.PENDING_CONFIRMATION:
        return 'warning';
      case RoomBookingStatus.CONFIRMED_BY_HOST:
        return 'success';
      case RoomBookingStatus.AUTO_CONFIRMED:
      case RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT:
        return 'success';
      case 'CONFIRMED': // Handle plain CONFIRMED status as success (green)
        return 'success';
      case RoomBookingStatus.CANCELLED_BY_USER:
        return 'default';
      case RoomBookingStatus.CANCELLED_BY_HOST:
        return 'error';
      case RoomBookingStatus.PENDING_PAYMENT:
        return 'error';
      case RoomBookingStatus.CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION:
        return 'error';
      case RoomBookingStatus.CANCELLED_DUE_NO_RESPONSE_FROM_HOST:
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper function to check if booking is cancelled
  const isBookingCancelled = (status: string | RoomBookingStatus): boolean => {
    if (!status) return false;
    const s = String(status).toUpperCase();
    return s === RoomBookingStatus.CANCELLED_BY_USER ||
           s === RoomBookingStatus.CANCELLED_BY_HOST ||
           s === RoomBookingStatus.CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION ||
           s === RoomBookingStatus.CANCELLED_DUE_NO_RESPONSE_FROM_HOST ||
           s === 'CANCELLED';
  };

  const handleCancelBooking = async () => {
    if (!id || !bookingDetails) return;
    
    setCancelling(true);
    try {
      await userApiClient.cancelRoomBooking(id);
      // Refresh booking details after cancellation
      const updatedDetails = await userApiClient.getRoomBookingDetails(id);
      setBookingDetails(updatedDetails);
      notifyRoomBookingNeedsActionCountChanged();
      setCancelDialogOpen(false);
    } catch (error) {
      console.error(t('bookingDetails.cancelError', 'Error cancelling booking:'), error);
    } finally {
      setCancelling(false);
    }
  };

  // Convert current time to UTC for proper comparison with booking end time (which is in UTC)
  const currentTimeUTC = new Date(currentTime.toISOString());

  const isPastBooking = bookingDetails && new Date(bookingDetails.endTime) < currentTimeUTC;

  const handleImageClick = () => {
    setSelectedImageIndex(activeStep);
  };

  const handleCloseGallery = () => {
    setSelectedImageIndex(null);
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null && bookingDetails) {
      setSelectedImageIndex((selectedImageIndex + 1) % bookingDetails.roomImageUrls.length);
    }
  };

  const handlePreviousImage = () => {
    if (selectedImageIndex !== null && bookingDetails) {
      setSelectedImageIndex((selectedImageIndex - 1 + bookingDetails.roomImageUrls.length) % bookingDetails.roomImageUrls.length);
    }
  };

  const handleCopyPasscode = async (passcode: string | null | undefined) => {
    if (!passcode) return;
    try {
      await navigator.clipboard.writeText(passcode);
      setPasscodeCopied(true);
      window.setTimeout(() => setPasscodeCopied(false), 1500);
    } catch (copyError) {
      console.error('Failed to copy passcode:', copyError);
    }
  };

  const openGoogleCalendar = () => {
    const url = bookingDetails?.calendarLinks?.google;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openIcsCalendar = async () => {
    const ics = bookingDetails?.calendarLinks?.ics;
    if (!ics || !bookingDetails) return;
    setCalendarIcsLoading(true);
    try {
      const blob = await userApiClient.fetchCalendarIcs(ics);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `booking-${bookingDetails.bookingId}.ics`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (calendarErr) {
      console.error(t('bookingDetails.calendarIcsError', 'Could not download calendar file.'), calendarErr);
    } finally {
      setCalendarIcsLoading(false);
    }
  };

  const handleCreateReview = async () => {
    if (!bookingDetails || reviewRating === 0) return;
    
    setSubmittingReview(true);
    setReviewError(null);
    
    try {
      const spaceReview = await userApiClient.createSpaceReview(bookingDetails.spaceId, {
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
        metadata: { source: 'booking_details' },
      });
      
      setHasReviewedSpace(true);
      setExistingReview(spaceReview);
      
      // Close dialog and reset form
      setReviewDialogOpen(false);
      setReviewRating(0);
      setReviewComment('');
      
      // If user was on the review route, navigate back to the regular booking details page
      if (location.pathname.endsWith('/review')) {
        navigate(`/user/bookings/${id}`, { replace: true });
      }
      
      // Refresh booking details
      if (id) {
        const updatedDetails = await userApiClient.getRoomBookingDetails(id);
        setBookingDetails(updatedDetails);
      }

      await refreshSpaceReviewStats(bookingDetails.spaceId);
      
    } catch (error) {
      console.error('Error creating review:', error);
      setReviewError(t('review.createError', 'Failed to create review. Please try again.'));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleOpenReviewDialog = () => {
    setReviewDialogOpen(true);
    setReviewError(null);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewRating(0);
    setReviewComment('');
    setReviewError(null);
    
    // If user was on the review route, navigate back to the regular booking details page
    if (location.pathname.endsWith('/review')) {
      navigate(`/user/bookings/${id}`, { replace: true });
    }
  };

  const handleBookAgain = async () => {
    if (!bookingDetails) return;
    
    setLoadingRoomSummary(true);
    try {
      const room = await apiClient.getRoomSummary(bookingDetails.roomId);
      setRoomSummary(room);
      setBookAgainDialogOpen(true);
    } catch (error) {
      console.error('Error fetching room summary:', error);
    } finally {
      setLoadingRoomSummary(false);
    }
  };

  const handleCloseBookAgainDialog = () => {
    setBookAgainDialogOpen(false);
  };

  // Change proposal handlers
  const handleOpenAcceptDialog = () => {
    setAcceptDialogOpen(true);
  };

  const handleAcceptChange = async () => {
    if (!bookingDetails || !bookingDetails.changeProposal || !id) return;
    if (isBookingCancelled(bookingDetails.status)) return;
    
    setProcessingChange(bookingDetails.changeProposal.id);
    setError(false);
    try {
      await userApiClient.acceptRoomBookingChange(
        id,
        bookingDetails.changeProposal.id,
        bookingDetails.recurringSeriesId,
      );
      // Refresh booking details
      const updatedDetails = await userApiClient.getRoomBookingDetails(id);
      setBookingDetails(updatedDetails);
      notifyRoomBookingNeedsActionCountChanged();
      setAcceptDialogOpen(false);
    } catch (error) {
      console.error('Error accepting change:', error);
      setError(true);
    } finally {
      setProcessingChange(null);
    }
  };

  const handleCloseAcceptDialog = () => {
    setAcceptDialogOpen(false);
  };

  const handleOpenDeclineDialog = () => {
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const handleDeclineChange = async () => {
    if (!bookingDetails || !bookingDetails.changeProposal || !id) return;
    if (isBookingCancelled(bookingDetails.status)) return;
    
    setProcessingChange(bookingDetails.changeProposal.id);
    setError(false);
    try {
      const request: DeclineChangeRequest = {
        reason: declineReason.trim() || undefined
      };
      await userApiClient.declineRoomBookingChange(
        id,
        bookingDetails.changeProposal.id,
        request,
        bookingDetails.recurringSeriesId,
      );
      // Refresh booking details
      const updatedDetails = await userApiClient.getRoomBookingDetails(id);
      setBookingDetails(updatedDetails);
      notifyRoomBookingNeedsActionCountChanged();
      setDeclineDialogOpen(false);
      setDeclineReason('');
    } catch (error) {
      console.error('Error declining change:', error);
      setError(true);
    } finally {
      setProcessingChange(null);
    }
  };

  const handleCloseDeclineDialog = () => {
    setDeclineDialogOpen(false);
    setDeclineReason('');
  };

  // Get change proposal status description
  const getChangeProposalStatusDescription = (status: RoomBookingChangeStatus): string => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return t('bookings.changeProposalStatus.pending_reply', 'Pending Reply');
      case RoomBookingChangeStatus.ACCEPTED:
        return t('bookings.changeProposalStatus.accepted', 'Accepted');
      case RoomBookingChangeStatus.DECLINED:
        return t('bookings.changeProposalStatus.declined', 'Declined');
      case RoomBookingChangeStatus.CANCELLED:
        return t('bookings.changeProposalStatus.cancelled', 'Cancelled');
      case RoomBookingChangeStatus.EXPIRED:
        return t('bookings.changeProposalStatus.expired', 'Expired');
      default:
        return status;
    }
  };

  // Get change proposal status color
  const getChangeProposalStatusColor = (status: RoomBookingChangeStatus) => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return 'warning';
      case RoomBookingChangeStatus.ACCEPTED:
        return 'success';
      case RoomBookingChangeStatus.DECLINED:
        return 'error';
      case RoomBookingChangeStatus.CANCELLED:
        return 'default';
      case RoomBookingChangeStatus.EXPIRED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getChangeProposalAutoAcceptAlert = (status: RoomBookingChangeStatus) => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return {
          severity: 'info' as const,
          message: t('bookings.changeProposal.autoAccept', 'Booking will be automatically approved if accepted'),
        };
      case RoomBookingChangeStatus.ACCEPTED:
        return {
          severity: 'success' as const,
          message: t(
            'bookings.changeProposal.autoAcceptAccepted',
            'When you accepted, the booking was confirmed automatically, as the host requested.',
          ),
        };
      case RoomBookingChangeStatus.DECLINED:
        return {
          severity: 'warning' as const,
          message: t(
            'bookings.changeProposal.autoAcceptDeclined',
            'The host had automatic confirmation on accept turned on—you declined, so that did not apply.',
          ),
        };
      default:
        return {
          severity: 'info' as const,
          message: t(
            'bookings.changeProposal.autoAcceptClosed',
            'This proposal included automatic confirmation if accepted; it no longer applies.',
          ),
        };
    }
  };

  const getChangeProposalReason = (proposal: RoomBookingChangeProposal): string | null => {
    const candidates = [proposal.proposalReason, proposal.reason, proposal.replyComment];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
    return null;
  };

  // Calculate new end time based on new start time and original duration
  const calculateNewEndTime = (newStartTime: string, originalStartTime: string, originalEndTime: string) => {
    const duration = calculateDuration(originalStartTime, originalEndTime);
    const newStart = parseApiUtcIso(newStartTime);
    const newEnd = new Date(newStart.getTime() + duration * 60 * 1000);
    return newEnd.toISOString();
  };

  const proposalTimeChanged = (proposal: RoomBookingChangeProposal) =>
    parseApiUtcIso(proposal.oldStartTime).getTime() !== parseApiUtcIso(proposal.newStartTime).getTime();

  const getProposalOldEndIso = (
    proposal: RoomBookingChangeProposal,
    bookingStart: string,
    bookingEnd: string,
  ) => {
    const durMin = calculateDuration(bookingStart, bookingEnd);
    const oldStartMs = parseApiUtcIso(proposal.oldStartTime).getTime();
    return new Date(oldStartMs + durMin * 60 * 1000).toISOString();
  };

  const getChangeProposalNewEndIso = (
    proposal: RoomBookingChangeProposal,
    bookingStart: string,
    bookingEnd: string,
  ) => {
    const syntheticOldEnd = getProposalOldEndIso(proposal, bookingStart, bookingEnd);
    return calculateNewEndTime(proposal.newStartTime, proposal.oldStartTime, syntheticOldEnd);
  };

  // Format date and time for UTC times (e.g., change proposals) - with duration
  const formatDateTimeWithDurationUTC = (startTime: string, endTime: string) => {
    if (!startTime) return '';
    
    const date = parseApiUtcIso(startTime);
    const dateTimeStr = date.toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const duration = formatDuration(calculateDuration(startTime, endTime));
    return `${dateTimeStr} (${duration})`;
  };

  const canWriteReview = !!bookingDetails &&
    (bookingDetails.status === RoomBookingStatus.CONFIRMED_BY_HOST ||
     bookingDetails.status === RoomBookingStatus.AUTO_CONFIRMED ||
     bookingDetails.status === RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT ||
     bookingDetails.status === 'CONFIRMED' ||
     bookingDetails.status === 'PAID' ||
     bookingDetails.status === 'COMPLETED') &&
    new Date(bookingDetails.endTime) < currentTimeUTC &&
    !hasReviewedSpace;
  const reviewPending =
    !!bookingDetails &&
    (bookingDetails.status === RoomBookingStatus.CONFIRMED_BY_HOST ||
      bookingDetails.status === RoomBookingStatus.AUTO_CONFIRMED ||
      bookingDetails.status === RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT ||
      bookingDetails.status === 'CONFIRMED' ||
      bookingDetails.status === 'PAID' ||
      bookingDetails.status === 'COMPLETED') &&
    new Date(bookingDetails.endTime) > currentTimeUTC &&
    !hasReviewedSpace;
  const bookingReason = bookingDetails?.reason?.trim();

  const renderSpaceReviewMeta = () => {
    if (isHost) return null;

    const reviewCount = spaceReviewStats?.reviewCount ?? 0;
    const avgReviewRating = spaceReviewStats?.averageRating ?? 0;
    const reviewAvailableAfterLabel = t(
      'bookingDetails.reviewAvailableAfter',
      'Review will be available after your booking ends',
    );
    const spaceRatingSx = {
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
      '& .MuiRating-icon': { fontSize: 18 },
      '& .MuiRating-iconFilled': { color: '#F8C14A' },
      '& .MuiRating-iconEmpty': { color: 'action.disabled' },
    } as const;
    const reviewMetaTextProps = {
      variant: 'body2' as const,
      color: 'text.secondary' as const,
      sx: { fontSize: '0.875rem', lineHeight: 1.4 },
    };
    const reviewStarsColumnSx = {
      width: 100,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    } as const;
    const renderReadOnlySpaceRating = (name: string, value = reviewCount > 0 ? avgReviewRating : 0) => (
      <Rating
        name={name}
        value={value}
        precision={0.1}
        readOnly
        size="small"
        icon={<StarRoundedIcon fontSize="inherit" />}
        emptyIcon={<StarRoundedIcon fontSize="inherit" />}
        sx={spaceRatingSx}
      />
    );
    const renderRatingSummary = (name: string) => {
      const reviewCountLabel = `(${t('review.reviewCount', { count: reviewCount })})`;

      return (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={reviewStarsColumnSx}>{renderReadOnlySpaceRating(name)}</Box>
          <Typography
            {...reviewMetaTextProps}
            component="span"
            sx={{
              ...reviewMetaTextProps.sx,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              lineHeight: '18px',
            }}
          >
            <span>{avgReviewRating.toFixed(1)}</span>
            {reviewCount > 0 ? (
              <Box
                component="button"
                type="button"
                onClick={() => setAllReviewsDialogOpen(true)}
                sx={{
                  border: 0,
                  bgcolor: 'transparent',
                  p: 0,
                  m: 0,
                  cursor: 'pointer',
                  color: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  fontFamily: 'inherit',
                  fontWeight: 'inherit',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {reviewCountLabel}
              </Box>
            ) : (
              <span>{reviewCountLabel}</span>
            )}
          </Typography>
        </Stack>
      );
    };
    const renderYourRating = () => {
      if (!existingReview) return null;

      const comment = existingReview.comment?.trim() || null;
      const row = (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          component="span"
          sx={{
            display: 'inline-flex',
            cursor: comment ? 'help' : 'default',
          }}
        >
          <Box sx={reviewStarsColumnSx}>
            {renderReadOnlySpaceRating('room-booking-your-rating', existingReview.rating)}
          </Box>
          <Typography {...reviewMetaTextProps} component="span" fontWeight={600}>
            {t('bookingDetails.ratedByYou', 'Rated by You')}
          </Typography>
        </Stack>
      );

      if (!comment) return row;

      return (
        <Tooltip
          title={comment}
          placement="top"
          arrow
          enterTouchDelay={0}
          slotProps={{
            tooltip: {
              sx: { maxWidth: 280, whiteSpace: 'pre-wrap' },
            },
          }}
        >
          {row}
        </Tooltip>
      );
    };

    if (canWriteReview) {
      return (
        <Stack spacing={0.75} alignItems="flex-start">
          {spaceReviewStats && reviewCount > 0 ? renderRatingSummary('room-booking-space-rating') : null}
          <Rating
            name="room-booking-review"
            value={0}
            onChange={(_, newValue) => {
              setReviewRating(newValue ?? 0);
              handleOpenReviewDialog();
            }}
            precision={1}
            size="medium"
            sx={{
              alignSelf: 'flex-start',
              '& .MuiRating-iconFilled': { color: '#F8C14A' },
              '& .MuiRating-iconHover': { color: '#F8C14A' },
            }}
          />
        </Stack>
      );
    }

    if (reviewPending) {
      return (
        <Box
          component="span"
          tabIndex={0}
          aria-label={reviewAvailableAfterLabel}
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'flex-start',
            gap: 0.5,
            cursor: 'help',
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
            mx: -0.5,
            outline: 'none',
            '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
            '& .review-available-hint': {
              opacity: 0,
              maxHeight: 0,
              overflow: 'hidden',
              transition: 'opacity 120ms ease, max-height 120ms ease',
            },
            '&:hover .review-available-hint, &:focus-visible .review-available-hint': {
              opacity: 1,
              maxHeight: 48,
            },
          }}
        >
          {spaceReviewStats !== null ? renderRatingSummary('room-booking-review-pending') : null}
          <Typography className="review-available-hint" variant="caption" color="text.secondary">
            {reviewAvailableAfterLabel}
          </Typography>
        </Box>
      );
    }

    if (hasReviewedSpace && existingReview) {
      return (
        <Stack spacing={0.75} alignItems="flex-start">
          {spaceReviewStats !== null ? renderRatingSummary('room-booking-space-rating-reviewed') : null}
          {renderYourRating()}
        </Stack>
      );
    }

    if (spaceReviewStats === null) return null;

    return renderRatingSummary('room-booking-space-rating');
  };

  const formatMoney = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatProposalScheduleLabels = (
    startTime: string,
    endTime: string,
  ): TutorBookingScheduleLabels => {
    const start = parseApiUtcIso(startTime);
    const end = parseApiUtcIso(endTime);
    const dateLabel = start.toLocaleDateString(i18n.language, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const timeOpts: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    return {
      dateLabel,
      timeLabel: `${start.toLocaleTimeString(i18n.language, timeOpts)}–${end.toLocaleTimeString(i18n.language, timeOpts)}`,
      durationLabel: formatDuration(Math.round((end.getTime() - start.getTime()) / (1000 * 60))),
    };
  };

  const proposal = bookingDetails?.changeProposal;
  const pendingProposal = proposal?.status === RoomBookingChangeStatus.PENDING_REPLY;
  const displayRoomName =
    proposal &&
    proposal.status === RoomBookingChangeStatus.ACCEPTED &&
    proposal.newRoomId !== proposal.oldRoomId &&
    proposal.newRoomName
      ? proposal.newRoomName
      : bookingDetails?.roomName ?? '';
  const visibleContacts = visibleOrganizationContacts(bookingDetails?.contacts);
  const hasOrganizationContact = visibleContacts.length > 0;

  const scheduleDisplay = (() => {
    if (!bookingDetails) {
      return {
        schedule: { dateLabel: '', timeLabel: '', durationLabel: '' },
        previousTimeLabel: undefined as string | undefined,
        emphasize: false,
      };
    }

    if (pendingProposal && proposal) {
      const proposedEndIso = getChangeProposalNewEndIso(
        proposal,
        bookingDetails.startTime,
        bookingDetails.endTime,
      );
      const proposedSchedule = formatProposalScheduleLabels(proposal.newStartTime, proposedEndIso);
      if (proposalTimeChanged(proposal)) {
        const oldEndIso = getProposalOldEndIso(
          proposal,
          bookingDetails.startTime,
          bookingDetails.endTime,
        );
        const previousSchedule = formatProposalScheduleLabels(proposal.oldStartTime, oldEndIso);
        return {
          schedule: proposedSchedule,
          previousTimeLabel: getTutorBookingPreviousTimeLabel(previousSchedule, proposedSchedule),
          emphasize: true,
        };
      }
      return { schedule: proposedSchedule, previousTimeLabel: undefined, emphasize: true };
    }

    const effectiveStart =
      proposal?.status === RoomBookingChangeStatus.ACCEPTED
        ? proposal.newStartTime
        : bookingDetails.startTime;
    const effectiveEnd =
      proposal?.status === RoomBookingChangeStatus.ACCEPTED
        ? getChangeProposalNewEndIso(proposal, bookingDetails.startTime, bookingDetails.endTime)
        : bookingDetails.endTime;

    if (proposal?.status === RoomBookingChangeStatus.ACCEPTED) {
      return {
        schedule: formatProposalScheduleLabels(effectiveStart, effectiveEnd),
        previousTimeLabel: undefined,
        emphasize: false,
      };
    }

    return {
      schedule: formatTutorBookingScheduleLabels(
        effectiveStart,
        effectiveEnd,
        i18n.language,
        formatDuration,
      ),
      previousTimeLabel: undefined,
      emphasize: false,
    };
  })();

  const showCalendarLinks =
    !!(bookingDetails?.calendarLinks?.google || bookingDetails?.calendarLinks?.ics) &&
    !!bookingDetails &&
    !isBookingCancelled(bookingDetails.status);

  const cachedProfile = !isHost ? getCachedProfileDisplay() : null;
  const bookerDisplayName =
    bookingDetails?.userName?.trim() ||
    (!isHost ? cachedProfile?.name?.trim() : '') ||
    '';
  const bookerImageUrl =
    bookingDetails?.userProfileImageUrl ||
    (!isHost ? cachedProfile?.profileImageUrl : null) ||
    null;
  const bookerBandName =
    bookingDetails?.bandName?.trim() ||
    bookingDetails?.participantDetails?.bandName?.trim() ||
    '';

  const proposalReason = proposal ? getChangeProposalReason(proposal) : null;
  const proposalRoomChanged =
    !!proposal &&
    proposal.newRoomId !== proposal.oldRoomId &&
    !!proposal.newRoomName;

  const changeProposalActions =
    !isHost &&
    proposal &&
    pendingProposal &&
    bookingDetails &&
    !isBookingCancelled(bookingDetails.status) ? (
      <Stack spacing={1}>
        {proposalReason ? (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {proposalReason}
          </Typography>
        ) : null}
        {proposalRoomChanged ? (
          <Typography variant="caption" color="info.main" fontWeight={600}>
            {t('bookings.changeProposal.newRoom', 'New Room')}: {proposal.newRoomName}
          </Typography>
        ) : null}
        {proposal.autoAcceptRoomBooking ? (
          <Alert severity={getChangeProposalAutoAcceptAlert(proposal.status).severity} sx={{ py: 0.5 }}>
            {getChangeProposalAutoAcceptAlert(proposal.status).message}
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleOpenAcceptDialog}
            disabled={processingChange === proposal.id}
            startIcon={
              processingChange === proposal.id ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ flex: 1 }}
          >
            {t('bookings.changeProposal.accept', 'Accept')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleOpenDeclineDialog}
            disabled={processingChange === proposal.id}
            sx={{ flex: 1 }}
          >
            {t('bookings.changeProposal.decline', 'Decline')}
          </Button>
        </Stack>
      </Stack>
    ) : null;

  return (
    <>
      <SEO title={t('bookingDetails.title', 'Room Booking Details')} noindex />
      <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 }, pb: { xs: 8, sm: 10 } }}>
        <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" sx={{ minWidth: 0 }}>
              {t('bookingDetails.title', 'Room Booking Details')}
            </Typography>
            <Chip
              label={
                isHost
                  ? `← ${t('roomBookingRequests.backToRequests', 'Back to Booking Requests')}`
                  : `← ${t('bookingDetails.backToBookings', 'Back to My Bookings')}`
              }
              onClick={() => navigate(bookingsListPath)}
              sx={{
                flexShrink: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                bgcolor: 'transparent',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'transparent' },
              }}
            />
          </Stack>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('roomBookingRequests.booker', 'Booker')}
                  </Typography>
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <RoomBookingUserAvatar
                        userProfileImageUrl={bookerImageUrl}
                        displayName={bookerDisplayName || bookerBandName || '?'}
                      />
                      <Stack spacing={0.75} minWidth={0} sx={{ flex: 1, pt: 0.25 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
                          <Typography variant="body1" fontWeight={700} noWrap>
                            {bookerDisplayName ||
                              (isHost
                                ? t('roomBookingRequests.guestBooker', 'Guest booker')
                                : t('common.you', 'You'))}
                          </Typography>
                          {isHost && bookingDetails.userId?.trim() ? (
                            <ChatWithUserIconButton
                              partnerUserId={bookingDetails.userId}
                              partnerName={bookerDisplayName || null}
                              basePath="/host/chats"
                              sx={{ flexShrink: 0 }}
                            />
                          ) : null}
                        </Stack>
                        {(bookingDetails.phoneNumber ||
                          bookingDetails.participantDetails?.participantCount) ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent={
                              bookingDetails.phoneNumber &&
                              bookingDetails.participantDetails?.participantCount
                                ? 'space-between'
                                : 'flex-start'
                            }
                            sx={{ width: '100%' }}
                          >
                            {bookingDetails.phoneNumber ? (
                              <Stack direction="row" spacing={0.35} alignItems="center" minWidth={0}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="a"
                                  href={`tel:${bookingDetails.phoneNumber}`}
                                  sx={{ textDecoration: 'none' }}
                                  noWrap
                                >
                                  {bookingDetails.phoneNumber}
                                </Typography>
                              </Stack>
                            ) : null}
                            {bookingDetails.participantDetails?.participantCount ? (
                              <Stack direction="row" spacing={0.35} alignItems="center" sx={{ flexShrink: 0 }}>
                                <GroupsRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  ×{bookingDetails.participantDetails.participantCount}
                                </Typography>
                              </Stack>
                            ) : null}
                          </Stack>
                        ) : null}
                        {bookerBandName ? <UserBandAffiliationLine bandName={bookerBandName} /> : null}
                      </Stack>
                    </Stack>
                    {bookingDetails.participantDetails?.details?.trim() ? (
                      <>
                        <Divider />
                        <Stack direction="row" spacing={1.25} alignItems="flex-start" minWidth={0}>
                          <Box
                            sx={{
                              width: 48,
                              flexShrink: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignSelf: 'center',
                            }}
                          >
                            <StickyNote2OutlinedIcon
                              sx={{
                                fontSize: 18,
                                color: 'primary.main',
                              }}
                            />
                          </Box>
                          <Stack spacing={0.25} minWidth={0} sx={{ flex: 1 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                              {t('bookingSteps.additionalDetails', 'Special Request')}
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                              {bookingDetails.participantDetails.details.trim()}
                            </Typography>
                          </Stack>
                        </Stack>
                      </>
                    ) : null}
                  </Stack>
                </Box>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    background:
                      'linear-gradient(160deg, rgba(78,103,235,0.08) 0%, rgba(78,103,235,0.02) 55%, transparent 100%)',
                  }}
                >
                  <Stack spacing={2} sx={{ flex: 1 }}>
                    <Chip
                      color={getBookingStatusColor(bookingDetails.status)}
                      label={t(`status.${bookingDetails.status.toLowerCase()}`, bookingDetails.status)}
                      sx={{
                        width: '100%',
                        height: 48,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        fontSize: '0.9375rem',
                        letterSpacing: '0.02em',
                        '& .MuiChip-label': {
                          px: 1.5,
                          width: '100%',
                          textAlign: 'center',
                        },
                      }}
                    />

                    {proposal && !pendingProposal ? (
                      <Chip
                        size="small"
                        label={getChangeProposalStatusDescription(proposal.status)}
                        color={getChangeProposalStatusColor(proposal.status) as 'default'}
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    ) : null}

                    <TutorBookingScheduleBlock
                      schedule={scheduleDisplay.schedule}
                      emphasize={scheduleDisplay.emphasize}
                      previousTimeLabel={scheduleDisplay.previousTimeLabel}
                      actions={changeProposalActions}
                    />

                    <Box
                      onClick={() => window.open(bookingDetails.spaceGoogleMapsUrl || '#', '_blank')}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                          bgcolor: 'background.paper',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <LocationOnIcon
                        sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0, alignSelf: 'center' }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                        {formatAddress(bookingDetails)}
                      </Typography>
                      <Tooltip title={t('chat.files.openInNewTab', 'Open in new tab')}>
                        <IconButton
                          size="small"
                          aria-label={t('chat.files.openInNewTab', 'Open in new tab')}
                          onClick={(event) => {
                            event.stopPropagation();
                            window.open(bookingDetails.spaceGoogleMapsUrl || '#', '_blank');
                          }}
                          sx={{
                            flexShrink: 0,
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {proposal && !pendingProposal && (proposalReason || proposalRoomChanged) ? (
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Stack spacing={0.75}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {t('bookings.changeProposal.title', 'Change Proposal')}
                          </Typography>
                          {proposalReason ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {proposalReason}
                            </Typography>
                          ) : null}
                          {proposalRoomChanged ? (
                            <Typography variant="body2" color="info.main" fontWeight={600}>
                              {t('bookings.changeProposal.newRoom', 'New Room')}: {proposal.newRoomName}
                            </Typography>
                          ) : null}
                          {proposal.autoAcceptRoomBooking ? (
                            <Alert
                              severity={getChangeProposalAutoAcceptAlert(proposal.status).severity}
                              sx={{ py: 0.5 }}
                            >
                              {getChangeProposalAutoAcceptAlert(proposal.status).message}
                            </Alert>
                          ) : null}
                        </Stack>
                      </Box>
                    ) : null}

                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Stack spacing={1}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <MeetingRoomRoundedIcon
                          sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Stack spacing={0.75} minWidth={0} sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {displayRoomName}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {bookingDetails.paymentMethod && !bookingDetails.pass ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t(
                                  `paymentMethod.${bookingDetails.paymentMethod.toLowerCase()}`,
                                  bookingDetails.paymentMethod,
                                )}
                                sx={{
                                  height: 22,
                                  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                                }}
                              />
                            ) : null}
                            <TutorBookingPaymentStatusChips
                              paymentStatus={bookingDetails.pass ? undefined : bookingDetails.paymentStatus}
                              stripeRefundStatus={bookingDetails.stripeRefundStatus ?? undefined}
                              compact
                            />
                            {bookingDetails.pass ? (
                              <PassBookingChip pass={bookingDetails.pass} />
                            ) : null}
                          </Stack>
                          {bookingReason ? (
                            <Typography variant="caption" color="text.secondary">
                              {bookingReason}
                            </Typography>
                          ) : null}
                        </Stack>
                        <Stack
                          spacing={0.15}
                          alignItems="flex-end"
                          justifyContent="center"
                          sx={{ flexShrink: 0, alignSelf: 'center' }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PaymentsRoundedIcon
                              sx={{
                                fontSize: 18,
                                color: bookingDetails.pass ? 'text.secondary' : 'text.primary',
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={bookingDetails.pass ? 'text.secondary' : 'text.primary'}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2,
                                textDecoration: bookingDetails.pass ? 'line-through' : 'none',
                              }}
                            >
                              {formatMoney(bookingDetails.totalPrice, bookingDetails.currencyCode)}
                            </Typography>
                          </Stack>
                          <BookingStripeFeeBreakdown
                            showStaffFees={isHost}
                            bookingTotalAmount={bookingDetails.totalPrice}
                            bookingCommissionPercent={bookingDetails.bookingCommissionPercent}
                            bookingCommissionAmount={bookingDetails.bookingCommissionAmount}
                            stripeProcessingFeeAmount={bookingDetails.stripeProcessingFeeAmount}
                            stripeProcessingFeeCurrency={
                              bookingDetails.stripeProcessingFeeCurrency
                            }
                            stripeReceiptUrl={bookingDetails.stripeReceiptUrl}
                            bookingCurrencyCode={bookingDetails.currencyCode}
                          />
                        </Stack>
                      </Stack>
                      </Stack>
                    </Box>

                    {showCalendarLinks && !isHost ? (
                      <Box
                        sx={{
                          py: 1.5,
                          px: 1.5,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <CalendarMonthOutlinedIcon
                              sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                            />
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
                              {t('bookingDetails.addToCalendar', 'Add to Calendar')}
                            </Typography>
                          </Stack>
                          <Box sx={{ pl: 3, width: '100%', boxSizing: 'border-box' }}>
                          <ToggleButtonGroup
                            exclusive
                            value={null}
                            onChange={(_event, value: string | null) => {
                              if (value === 'google') {
                                openGoogleCalendar();
                              } else if (value === 'apple' || value === 'ics') {
                                void openIcsCalendar();
                              }
                            }}
                            sx={{
                              width: '100%',
                              display: 'flex',
                              '& .MuiToggleButton-root': {
                                flex: 1,
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.75,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                lineHeight: 1.25,
                                textTransform: 'none',
                                gap: 0.75,
                                borderColor: 'divider',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                '&.Mui-selected': {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                },
                                '&.Mui-selected:hover': {
                                  bgcolor: 'action.hover',
                                },
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                              },
                            }}
                          >
                            {bookingDetails.calendarLinks?.google ? (
                              <ToggleButton value="google" title={t('bookingDetails.calendarGoogle', 'Google Calendar')}>
                                <Google sx={{ fontSize: 16 }} />
                                Google
                              </ToggleButton>
                            ) : null}
                            {bookingDetails.calendarLinks?.ics ? (
                              <ToggleButton
                                value="apple"
                                disabled={calendarIcsLoading}
                                title={t('bookingDetails.calendarApple', 'Apple Calendar')}
                              >
                                {calendarIcsLoading ? (
                                  <CircularProgress color="inherit" size={14} />
                                ) : (
                                  <Apple sx={{ fontSize: 16 }} />
                                )}
                                Apple
                              </ToggleButton>
                            ) : null}
                            {bookingDetails.calendarLinks?.ics ? (
                              <ToggleButton
                                value="ics"
                                disabled={calendarIcsLoading}
                                title={t('bookingDetails.calendarIcs', 'ICS file')}
                              >
                                {calendarIcsLoading ? (
                                  <CircularProgress color="inherit" size={14} />
                                ) : (
                                  <DescriptionIcon sx={{ fontSize: 16 }} />
                                )}
                                ICS
                              </ToggleButton>
                            ) : null}
                          </ToggleButtonGroup>
                          </Box>
                        </Stack>
                      </Box>
                    ) : null}

                    {bookingDetails.passcodeInfo ? (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {t('bookingDetails.accessInstructions', 'Access Instructions')}
                        </Typography>
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <KeyIcon sx={{ color: 'primary.main' }} fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              {t('bookingDetails.smartLockPasscode', 'Passcode')}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {bookingDetails.passcodeInfo.passcode ? (
                              <Tooltip
                                title={
                                  passcodeCopied
                                    ? t('bookingDetails.passcodeCopied', 'Copied')
                                    : t('bookingDetails.copyPasscode', 'Copy passcode')
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyPasscode(bookingDetails.passcodeInfo?.passcode)}
                                  aria-label={t('bookingDetails.copyPasscode', 'Copy passcode')}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                fontFamily: 'monospace',
                                lineHeight: 1,
                              }}
                            >
                              {bookingDetails.passcodeInfo.passcode ??
                                t('bookingDetails.smartLockPasscodeUnavailable', 'Unavailable')}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <AccessTimeIcon sx={{ color: 'text.secondary', mt: '2px' }} fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('bookingDetails.smartLockPasscodeWindow', 'Access window')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                {formatPasscodeWindow(
                                  bookingDetails.passcodeInfo.validFrom,
                                  bookingDetails.passcodeInfo.validTo,
                                )}
                              </Typography>
                            </Box>
                          </Stack>
                          {bookingDetails.passcodeInfo.status === BookingPasscodeStatus.FAILED ? (
                            <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />} sx={{ py: 0.5 }}>
                              {t(
                                'bookingDetails.smartLockPasscodeFailed',
                                'Passcode generation failed. Please contact the host.',
                              )}
                            </Alert>
                          ) : null}
                        </Stack>
                      </Box>
                    ) : null}

                    {!isBookingCancelled(bookingDetails.status) && !isPastBooking && !isHost ? (
                      <>
                        {shouldShowCancellationPolicyForPayment(bookingDetails.paymentStatus) ? (
                          <BookingCancellationPolicy
                            startTime={bookingDetails.startTime}
                            timezone={bookingDetails.timezone ?? bookingDetails.spaceTimezone}
                            policy={bookingDetails.cancellationPolicy}
                            showWhenMissing={false}
                            density="comfortable"
                          />
                        ) : null}
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => setCancelDialogOpen(true)}
                          fullWidth
                        >
                          {userRoomCancelReleasesSeries(bookingDetails)
                            ? t('bookings.cancelAllSessions', 'Cancel all')
                            : t('bookings.cancelBooking', 'Cancel Booking')}
                        </Button>
                        <BookingCreatedAtLabel
                          createdAt={bookingDetails.createdAt}
                          timezone={bookingDetails.timezone ?? bookingDetails.spaceTimezone}
                          placement="footer"
                        />
                      </>
                    ) : (
                      <BookingCreatedAtLabel
                        createdAt={bookingDetails.createdAt}
                        timezone={bookingDetails.timezone ?? bookingDetails.spaceTimezone}
                        placement="footer"
                      />
                    )}
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={8}>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 2, sm: 3 }}
                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        flexShrink: 0,
                        width: { xs: '100%', sm: 160, md: 180 },
                        maxWidth: { xs: 280, sm: 160, md: 180 },
                        height: { xs: 180, sm: 160, md: 180 },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <MediaDisplay
                          src={bookingDetails.roomImageUrls[activeStep] || '/logo512.png'}
                          alt={t('bookingDetails.roomImage', 'Room image {{number}}', {
                            number: activeStep + 1,
                          })}
                          onClick={handleImageClick}
                          sx={{
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.02)' },
                          }}
                        />
                        {bookingDetails.roomImageUrls.length > 1 ? (
                          <MobileStepper
                            variant="dots"
                            steps={bookingDetails.roomImageUrls.length}
                            activeStep={activeStep}
                            position="static"
                            nextButton={
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setActiveStep((s) =>
                                    Math.min(s + 1, bookingDetails.roomImageUrls.length - 1),
                                  )
                                }
                                disabled={activeStep === bookingDetails.roomImageUrls.length - 1}
                                sx={{
                                  position: 'absolute',
                                  right: 4,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255,255,255,0.75)',
                                }}
                              >
                                <KeyboardArrowRight />
                              </IconButton>
                            }
                            backButton={
                              <IconButton
                                size="small"
                                onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                                disabled={activeStep === 0}
                                sx={{
                                  position: 'absolute',
                                  left: 4,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255,255,255,0.75)',
                                }}
                              >
                                <KeyboardArrowLeft />
                              </IconButton>
                            }
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              width: '100%',
                              bgcolor: 'transparent',
                            }}
                          />
                        ) : null}
                      </Box>
                      {spaceThumbnailUrl ? (
                        <Tooltip title={bookingDetails.spaceName} placement="top">
                          <Box
                            sx={{
                              position: 'absolute',
                              right: -4,
                              bottom: -4,
                              zIndex: 2,
                              width: { xs: 52, sm: 56 },
                              height: { xs: 52, sm: 56 },
                              borderRadius: 1.25,
                              overflow: 'hidden',
                              border: '2px solid',
                              borderColor: 'background.paper',
                              boxShadow: (theme) => theme.shadows[4],
                              bgcolor: 'background.paper',
                            }}
                          >
                            <MediaDisplay
                              src={spaceThumbnailUrl}
                              alt={bookingDetails.spaceName}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        </Tooltip>
                      ) : null}
                    </Box>

                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={0}
                      flex={1}
                      minWidth={0}
                      sx={{ width: '100%' }}
                      alignItems="stretch"
                    >
                      <Stack spacing={1.25} flex={hasOrganizationContact ? 5 : 1} minWidth={0}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Box minWidth={0} flex={1}>
                            <Typography
                              variant="h4"
                              sx={{
                                lineHeight: 1.15,
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                              }}
                            >
                              {displayRoomName}
                            </Typography>
                            {isHost ? (
                              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                                {bookingDetails.spaceName}
                              </Typography>
                            ) : (
                              <Box sx={{ mt: 0.5 }}>
                                <SpaceDetailLink
                                  spaceId={bookingDetails.spaceId}
                                  spaceName={bookingDetails.spaceName}
                                  spaceCity={bookingDetails.spaceCity}
                                />
                              </Box>
                            )}
                          </Box>
                          {isHost ? null : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<ReplayRoundedIcon />}
                              onClick={handleBookAgain}
                              disabled={loadingRoomSummary}
                              sx={{ flexShrink: 0 }}
                            >
                              {loadingRoomSummary
                                ? t('common.loading', 'Loading...')
                                : t('bookingDetails.bookAgain', 'Book again')}
                            </Button>
                          )}
                        </Stack>
                        {spaceDescription ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                          >
                            {spaceDescription}
                          </Typography>
                        ) : null}

                      {renderSpaceReviewMeta()}

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
                        <Chip
                          icon={<SquareFootRoundedIcon />}
                          label={`${bookingDetails.roomArea} ${bookingDetails.roomAreaUnit}`}
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>

                        {hasOrganizationContact ? (
                          <>
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{
                                display: { xs: 'none', md: 'block' },
                                mx: 2,
                                borderColor: 'divider',
                                opacity: 0.35,
                              }}
                            />
                            <Divider
                              sx={{
                                display: { xs: 'block', md: 'none' },
                                my: 2,
                                borderColor: 'divider',
                                opacity: 0.35,
                              }}
                            />
                            <Box sx={{ flex: 2, minWidth: { md: 148 } }}>
                          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.15 }}>
                            {t('space.contact', 'Contact')}
                          </Typography>
                          <Stack spacing={1.25}>
                            {visibleContacts.map((contact, index) => {
                              const ContactIcon = organizationContactIcon(contact);
                              const href = organizationContactHref(contact);
                              const opensInSameTab = organizationContactOpensInSameTab(contact);
                              return (
                              <Box
                                key={`contact-${contact.type}-${index}`}
                                component="a"
                                href={href}
                                target={opensInSameTab ? '_self' : '_blank'}
                                rel={opensInSameTab ? undefined : 'noopener noreferrer'}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.25,
                                  p: 1.5,
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: 'primary.main',
                                  },
                                }}
                              >
                                <ContactIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
                                <Box minWidth={0}>
                                  <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                                    {contact.value}
                                  </Typography>
                                  {contact.comment?.trim() ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                      {contact.comment.trim()}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                              );
                            })}
                          </Stack>
                            </Box>
                          </>
                        ) : null}
                    </Stack>
                  </Stack>
                </Box>

                {(bookingDetails.roomAmenities.length > 0 ||
                  includedRoomGears.length > 0 ||
                  bookingDetails.additionalGears.length > 0) && (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm:
                          bookingDetails.roomAmenities.length > 0 &&
                          (includedRoomGears.length > 0 || bookingDetails.additionalGears.length > 0)
                            ? '1fr 1fr'
                            : '1fr',
                      },
                    }}
                  >
                    {bookingDetails.roomAmenities.length > 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="overline" color="text.secondary">
                          {t('room.amenities', 'Amenities')}
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                          {bookingDetails.roomAmenities.map((amenity, index) => {
                            const icon = amenityIcons[amenity.amenityType] || <AddIcon fontSize="small" />;
                            return (
                              <Chip
                                key={`amenity-${index}`}
                                icon={icon}
                                size="small"
                                label={`${getAmenityLabel(amenity.amenityType)}${amenity.details ? ': ' + amenity.details : ''}`}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    ) : null}

                    {includedRoomGears.length > 0 || bookingDetails.additionalGears.length > 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Stack direction="row" spacing={0.55} alignItems="center">
                          <SpeakerRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                          <Typography variant="overline" color="text.secondary">
                            {includedRoomGears.length > 0
                              ? t('booking.includedGears', 'Included Gears')
                              : t('bookingDetails.additionalGear', 'Additional Rented Gear')}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                          {includedRoomGears.map((gear, index) => {
                            const icon = gearIcons[gear.gearType] || <AddIcon fontSize="small" />;
                            return (
                              <Chip
                                key={`gear-${index}`}
                                icon={icon}
                                size="small"
                                label={`${getGearLabel(gear.gearType)}${gear.details ? ': ' + gear.details : ''}`}
                              />
                            );
                          })}
                          {bookingDetails.additionalGears.map((gear, index) => (
                            <Chip
                              key={`extra-${gear.source}:${gear.id}:${index}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={`${getGearLabel(gear.gearType)}${gear.details ? ': ' + gear.details : ''}`}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                  </Box>
                )}

                {bookingDetails.roomDescription?.trim() ? (
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('bookingDetails.description', 'Room Description')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {bookingDetails.roomDescription}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </Grid>
          </Grid>

        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
        >
          <DialogTitle>
            {bookingDetails && userRoomCancelReleasesSeries(bookingDetails)
              ? t('bookings.cancelAllSessionsTitle', 'Cancel all sessions?')
              : t('bookings.cancelBooking', 'Cancel Booking')}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {bookingDetails && userRoomCancelReleasesSeries(bookingDetails)
                ? t(
                    'bookings.cancelOnlineSeriesHold',
                    'These sessions are still waiting for approval. Cancelling releases the card hold for the whole series.',
                  )
                : t('bookings.cancelConfirmation', 'Are you sure you want to cancel this booking? This action cannot be undone.')}
            </Typography>
            {bookingDetails &&
              shouldShowCancellationPolicyForPayment(bookingDetails.paymentStatus) && (
              <BookingCancellationPolicy
                startTime={bookingDetails.startTime}
                timezone={bookingDetails.timezone ?? bookingDetails.spaceTimezone}
                policy={bookingDetails.cancellationPolicy}
                showWhenMissing={false}
                density="comfortable"
                sx={{ mt: 1.5 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              {t('bookings.keepBooking', 'No, Keep Booking')}
            </Button>
            <Button 
              onClick={handleCancelBooking} 
              color="error" 
              variant="contained"
              disabled={cancelling || isPastBooking}
            >
              {cancelling ? t('bookings.cancelling', 'Cancelling...') : t('bookings.confirmCancel', 'Yes, Cancel Booking')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Gallery Dialog */}
        <Dialog
          open={selectedImageIndex !== null}
          onClose={handleCloseGallery}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
                          {selectedImageIndex !== null && bookingDetails && (
                <>
                  {isVideoUrl(bookingDetails.roomImageUrls[selectedImageIndex]) ? (
                    <VideoPlayer
                      src={bookingDetails.roomImageUrls[selectedImageIndex]}
                      alt="Gallery view"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '90vh',
                      }}
                    />
                  ) : (
                    <MediaDisplay
                      src={bookingDetails.roomImageUrls[selectedImageIndex]}
                      alt="Gallery view"
                      showPlayButton={false}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                <IconButton
                  onClick={handlePreviousImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  <KeyboardArrowRight />
                </IconButton>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* All space reviews */}
        {!isHost && (
          <Dialog
            open={allReviewsDialogOpen}
            onClose={() => setAllReviewsDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {t('review.allReviews', 'All Reviews')} ({spaceReviewStats?.reviewCount ?? spaceReviews.length})
                </Typography>
                <IconButton
                  aria-label={t('common.close', 'Close')}
                  onClick={() => setAllReviewsDialogOpen(false)}
                  sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 2, pt: 0 }}>
              {spaceReviewStats && spaceReviewStats.reviewCount > 0 ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Rating
                    value={spaceReviewStats.averageRating}
                    readOnly
                    precision={0.1}
                    size="small"
                    icon={<StarRoundedIcon fontSize="inherit" />}
                    emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                    sx={{
                      '& .MuiRating-iconFilled': { color: '#F8C14A' },
                      '& .MuiRating-iconEmpty': { color: 'action.disabled' },
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {spaceReviewStats.averageRating.toFixed(1)} ·{' '}
                    {t('review.reviewCount', { count: spaceReviewStats.reviewCount })}
                  </Typography>
                </Stack>
              ) : null}
              <Stack spacing={2} divider={<Divider flexItem />}>
                {[...spaceReviews]
                  .sort(
                    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                  )
                  .map((review) => (
                    <Box key={review.id}>
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <RoomBookingUserAvatar
                          userProfileImageUrl={review.profileImageUrl}
                          displayName={review.username?.trim() || '?'}
                          size={36}
                        />
                        <Box minWidth={0} sx={{ flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {review.username?.trim() ||
                                t('review.anonymousReviewer', 'Anonymous student')}
                            </Typography>
                            <Rating value={review.rating} readOnly size="small" precision={0.1} />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(review.createdAt).toLocaleDateString(i18n.language, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </Typography>
                          </Stack>
                          {review.comment?.trim() ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {review.comment.trim()}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            </DialogContent>
          </Dialog>
        )}

        {/* Review Dialog */}
        {!isHost && (
        <Dialog
          open={reviewDialogOpen} 
          onClose={handleCloseReviewDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              margin: { xs: 1, sm: 2 },
              maxWidth: { xs: 'calc(100% - 16px)', sm: '600px' },
              boxShadow: mode === 'dark' 
                ? '0px 8px 32px rgba(0, 0, 0, 0.6)' 
                : '0px 4px 20px rgba(0, 0, 0, 0.12)',
              background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
              border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
            }
          }}
        >
          {/* Header Section */}
          <Box sx={{ 
            p: { xs: 1.5, md: 2 }, 
            borderBottom: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
            bgcolor: mode === 'dark' ? '#1F2129' : '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  minWidth: 0,
                  color: mode === 'dark' ? '#ECECFB' : '#212529',
                  letterSpacing: '-0.02em'
                }}
                noWrap
                title={bookingDetails?.spaceName}
              >
                {bookingDetails?.spaceName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="overline" sx={{ color: 'grey.400', letterSpacing: 2, display: { xs: 'none', sm: 'block' } }}>
                {canWriteReview ? t('review.writeReview', 'REVIEW') : t('review.cannotReview', 'REVIEW')}
              </Typography>
              <IconButton aria-label="close" onClick={handleCloseReviewDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content Section */}
          <DialogContent sx={{ 
            pt: 3, 
            px: 3, 
            pb: 2,
            bgcolor: mode === 'dark' ? '#1F2129' : '#FFFFFF'
          }}>
            {canWriteReview ? (
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  gap: 2
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                    {t('review.rateYourExperience', 'Rate your experience')}
                  </Typography>
                  <Rating
                    value={reviewRating}
                    onChange={(event, newValue) => {
                      setReviewRating(newValue || 0);
                    }}
                    size="large"
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: '#4E67EB',
                      },
                      '& .MuiRating-iconHover': {
                        color: '#3d56d9',
                      },
                    }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#ECECFB' : '#212529' }}>
                    {reviewRating}/5
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('review.comment', 'Share your experience (optional)')}
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder={t('review.commentPlaceholder', 'Tell others about your experience with this space...')}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                      '&:hover': {
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
                      },
                      '&.Mui-focused': {
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
                      }
                    }
                  }}
                />

                {reviewError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {reviewError}
                  </Alert>
                )}
              </Box>
            ) : (
              <Box sx={{ 
                p: 3,
                borderRadius: 2,
                backgroundColor: mode === 'dark' ? 'rgba(79, 95, 235, 0.1)' : 'rgba(79, 95, 235, 0.05)',
                border: `1px solid ${mode === 'dark' ? 'rgba(79, 95, 235, 0.3)' : 'rgba(79, 95, 235, 0.2)'}`
              }}>
                <Alert 
                  severity="info" 
                  sx={{ 
                    backgroundColor: 'transparent',
                    border: 'none',
                    '& .MuiAlert-message': {
                      color: mode === 'dark' ? '#ECECFB' : '#212529',
                      fontWeight: 500
                    }
                  }}
                >
                  {existingReview ? (
                    t('review.alreadyReviewed', 'You have already written a review for this space.')
                  ) : new Date(bookingDetails?.endTime || 0) >= new Date(new Date().toISOString()) ? (
                    t('review.bookingNotEnded', 'You can only write a review after your booking has ended.')
                  ) : (
                    t('review.bookingNotEligible', 'This booking is not eligible for review. Only confirmed and completed bookings can be reviewed.')
                  )}
                </Alert>
              </Box>
            )}
          </DialogContent>

          {/* Actions Section */}
          {canWriteReview && (
            <Box sx={{ 
              p: 3, 
              borderTop: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
              bgcolor: mode === 'dark' ? '#1F2129' : '#FFFFFF',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button 
                onClick={handleCreateReview} 
                variant="contained"
                disabled={submittingReview || reviewRating === 0}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  backgroundColor: '#4E67EB',
                  color: '#ffffff',
                  boxShadow: '0 2px 8px rgba(78, 103, 235, 0.25)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#3d56d9',
                    boxShadow: '0 4px 12px rgba(78, 103, 235, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                  }
                }}
              >
                {submittingReview ? t('review.submitting', 'SUBMITTING...') : t('review.submit', 'SUBMIT REVIEW')}
              </Button>
            </Box>
          )}
        </Dialog>
        )}

        {/* Book Again Dialog */}
        {!isHost && roomSummary && (
          <RoomBookingStepperDialog
            open={bookAgainDialogOpen}
            onClose={handleCloseBookAgainDialog}
            room={roomSummary}
            spaceName={bookingDetails?.spaceName}
            initialDateTime={new Date()}
            initialDuration={bookingDetails ? calculateDuration(bookingDetails.startTime, bookingDetails.endTime) : undefined}
          />
        )}

        {/* Accept Change Proposal Dialog */}
        <Dialog 
          key={`accept-dialog-${i18n.language}`}
          open={acceptDialogOpen} 
          onClose={handleCloseAcceptDialog}
          maxWidth="sm"
          fullWidth
          transitionDuration={0}
        >
          <DialogTitle>{t('bookings.changeProposal.acceptTitle', 'Accept Change Proposal')}</DialogTitle>
          <DialogContent>
            <Typography key={`accept-message-${i18n.language}`} sx={{ mb: 2 }}>
              {bookingDetails?.changeProposal?.autoAcceptRoomBooking
                ? t('bookings.changeProposal.acceptMessageWithAutoApprove', 'Are you sure you want to accept this change proposal? The booking will be updated with the new details and automatically approved.')
                : t('bookings.changeProposal.acceptMessage', 'Are you sure you want to accept this change proposal? The booking will be updated with the new details.')}
            </Typography>
            {bookingDetails?.changeProposal && (() => {
              const roomChanged = bookingDetails.changeProposal.newRoomId !== bookingDetails.changeProposal.oldRoomId;
              const timeChanged = proposalTimeChanged(bookingDetails.changeProposal);
              
              if (!roomChanged && !timeChanged) return null;
              
              return (
                <Box 
                  sx={{ 
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.08)' : 'rgba(79, 95, 235, 0.04)',
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.3)' : 'rgba(79, 95, 235, 0.2)',
                  }}
                >
                  {roomChanged && bookingDetails.changeProposal.newRoomName && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('bookings.room', 'Room')}:
                      </Typography>
                      <Typography variant="body2">
                        <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
                          {bookingDetails.roomName}
                        </Box>
                        <Box component="span" fontWeight={600}>
                          {bookingDetails.changeProposal.newRoomName}
                        </Box>
                      </Typography>
                    </Box>
                  )}
                  {timeChanged && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('bookings.dateTime', 'Date & Time')}:
                      </Typography>
                      <Typography variant="body2">
                        <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
                          {formatDateTimeWithDurationUTC(
                            bookingDetails.changeProposal.oldStartTime,
                            getProposalOldEndIso(
                              bookingDetails.changeProposal,
                              bookingDetails.startTime,
                              bookingDetails.endTime,
                            ),
                          )}
                        </Box>
                        <Box component="span" fontWeight={600}>
                          {formatDateTimeWithDurationUTC(
                            bookingDetails.changeProposal.newStartTime,
                            getChangeProposalNewEndIso(
                              bookingDetails.changeProposal,
                              bookingDetails.startTime,
                              bookingDetails.endTime,
                            ),
                          )}
                        </Box>
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseAcceptDialog} 
              disabled={processingChange !== null}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleAcceptChange} 
              color="success" 
              variant="contained"
              disabled={processingChange !== null}
              startIcon={processingChange ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('bookings.changeProposal.confirmAccept', 'Accept')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Decline Change Proposal Dialog */}
        <Dialog 
          key={`decline-dialog-${i18n.language}`}
          open={declineDialogOpen} 
          onClose={handleCloseDeclineDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('bookings.changeProposal.declineTitle', 'Decline Change Proposal')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t('bookings.changeProposal.declineMessage', 'Are you sure you want to decline this change proposal? You can optionally provide a reason.')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('bookings.changeProposal.declineReason', 'Reason (optional)')}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder={t('bookings.changeProposal.declineReasonPlaceholder', 'Enter your reason for declining...')}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDeclineDialog} 
              disabled={processingChange !== null}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeclineChange} 
              color="error" 
              variant="contained"
              disabled={processingChange !== null}
              startIcon={processingChange ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('bookings.changeProposal.confirmDecline', 'Decline')}
            </Button>
          </DialogActions>
        </Dialog>
        </Card>
      </Box>
    </>
  );
}
